import { buildApiUrl, getApiHeaders } from './queriesHelpers';
import { store } from './store';
import Cookies from 'js-cookie';

export type DcloudSearchResponse = {
  content?: any[];
  items?: any[];
  records?: any[];
  results?: any[];
  [key: string]: any;
};

function normalizeDcloudRows(data: any): any[] {
  if (!data) return [];

  if (Array.isArray(data)) return data;

  if (Array.isArray(data.content)) return data.content;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.records)) return data.records;
  if (Array.isArray(data.results)) return data.results;

  return [];
}

function cleanProductName(productName: string): string {
  let cleaned = String(productName || '').replace(/Cisco/gi, '').trim();

  if (cleaned.includes('Duo')) {
    cleaned = 'Duo';
  }

  return cleaned;
}

// -----------------------------------------------------------------------------
// Data centers
//
// dCloud sessions live in the data center they were scheduled in, so every call
// carries the caller's preferred data center. The backend proxy only honors the
// five known dCloud hosts.
// -----------------------------------------------------------------------------

export const DCLOUD_DATA_CENTER_COOKIE = 'preferred_data_center';

export const DCLOUD_DATA_CENTERS: { value: string; label: string }[] = [
  { value: 'rtp', label: 'RTP (US East)' },
  { value: 'sjc', label: 'San Jose (US West)' },
  { value: 'lon', label: 'London (EMEAR)' },
  { value: 'syd', label: 'Sydney (APJC)' },
  { value: 'sng', label: 'Singapore (APJC)' },
];

export const getPreferredDataCenter = (): string =>
  Cookies.get(DCLOUD_DATA_CENTER_COOKIE) || 'rtp';

export const setPreferredDataCenter = (dataCenter: string): void => {
  if (!dataCenter) return;

  Cookies.set(DCLOUD_DATA_CENTER_COOKIE, dataCenter, { sameSite: 'lax' });
};

function getPreferredDataCenterFallback(): string {
  return getPreferredDataCenter();
}

function getCurrentUserEmail(): string | null {
  try {
    const state: any = store.getState();
    return state?.user?.userData?.email || null;
  } catch {
    return null;
  }
}

// -----------------------------------------------------------------------------
// Request layer
// -----------------------------------------------------------------------------

export type DcloudAuthSource = 'user' | 'app_client_credentials' | 'unknown';

export type DcloudResult<T> = {
  data: T;
  /**
   * Which identity the backend proxy used upstream, read from the
   * X-DCloud-Auth-Source response header.
   */
  authSource: DcloudAuthSource;
  status: number;
};

export type DcloudRequestOptions = {
  params?: Record<string, any>;
  body?: any;
  /** Overrides the stored data center preference for this call. */
  dataCenter?: string;
  /** Auth/link endpoints are Toolbox routes, not dCloud passthrough. */
  skipDataCenter?: boolean;
  signal?: AbortSignal;
};

function parseAuthSource(value: string | null): DcloudAuthSource {
  if (value === 'user' || value === 'app_client_credentials') {
    return value;
  }

  return 'unknown';
}

export type DcloudRequestMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * The one request path to the dCloud backend. Exported so sibling modules
 * (Topology Builder) reuse the same email/data-center/auth-header handling
 * instead of growing a second, subtly different one.
 */
export async function dcloudFetch<T = any>(
  method: DcloudRequestMethod,
  endpoint: string,
  options: DcloudRequestOptions = {},
): Promise<DcloudResult<T>> {
  const { params = {}, body, dataCenter, skipDataCenter, signal } = options;

  const email = getCurrentUserEmail();

  const queryParams: Record<string, any> = {
    ...(email ? { email } : {}),
    ...(skipDataCenter
      ? {}
      : { preferred_data_center: dataCenter || getPreferredDataCenter() }),
    ...params,
  };

  const response = await fetch(buildApiUrl(endpoint, queryParams, false), {
    method,
    headers: getApiHeaders(null, method, endpoint),
    credentials: 'include',
    signal,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  const rawText = await response.text();

  let parsed: any = null;

  try {
    parsed = rawText ? JSON.parse(rawText) : null;
  } catch {
    parsed = rawText;
  }

  if (!response.ok) {
    // Topology Builder and dCloud both answer with a bare JSON array of
    // {logref, message} - the shape their own UI reads - so the first element's
    // message is the only human-readable text in the response.
    const listed = Array.isArray(parsed)
      ? parsed
          .map((entry: any) => entry?.message)
          .filter(Boolean)
          .join(' ')
      : null;

    // A body that did not parse is not a message. An unrouted path reaches
    // dCloud's own Tomcat, which answers with an HTML error page, and pasting
    // that into the UI tells the user nothing.
    const structured =
      listed ||
      parsed?.error ||
      parsed?.detail ||
      parsed?.message ||
      (Array.isArray(parsed?.message) ? parsed.message.join(' ') : null);

    const plain =
      typeof parsed === 'string' && !/^\s*</.test(parsed) ? parsed.trim() : null;

    const message =
      structured || plain || `dCloud request failed (${response.status})`;

    // Do not call refreshOn401 here. dCloud lookup failure should not
    // reset Toolbox session or reload the page.
    const error: any = new Error(String(message));
    error.status = response.status;
    error.payload = parsed;
    error.rawBody = rawText;
    throw error;
  }

  return {
    data: parsed as T,
    authSource: parseAuthSource(response.headers.get('x-dcloud-auth-source')),
    status: response.status,
  };
}

async function dcloudGet<T = any>(
  endpoint: string,
  params: Record<string, any> = {},
): Promise<T> {
  const { data } = await dcloudFetch<T>('GET', endpoint, { params });
  return data;
}

/**
 * Deprecated compatibility no-op.
 *
 * Backend now owns dCloud OAuth token warmup/cache/refresh.
 * Frontend should not request or store a dCloud access token.
 */
export const generateToken = async (_email?: string | null): Promise<void> => {
  Cookies.remove('dcloud');
};

/**
 * Fetch product demos through toolbox_dcloud backend proxy.
 *
 * Browser sends Toolbox JWT via getApiHeaders().
 * Backend uses cached/warmed dCloud OAuth token internally.
 */
export const fetchProductDemos = async (
  productName: string,
  email?: string | null,
): Promise<any> => {
  const text = cleanProductName(productName);

  if (!text) {
    return { content: [] };
  }

  const preferredDataCenter = getPreferredDataCenterFallback();

  const data = await dcloudGet<DcloudSearchResponse>(
    'dcloud/catalog/search',
    {
      text,
      ...(email ? { email } : {}),
      preferred_data_center: preferredDataCenter,
    },
  );

  return {
    ...data,
    content: normalizeDcloudRows(data),
  };
};

export const groupDemos = (demos: any[] = []) => {
  const rows = Array.isArray(demos) ? demos : [];

  const hasFilter = (demo: any, id: number) => {
    const filterIds = Array.isArray(demo?.filterIds) ? demo.filterIds : [];
    return filterIds.includes(id);
  };

  const instantDemos = rows.filter((demo) => hasFilter(demo, 453));
  const guidedDemos = rows.filter((demo) => hasFilter(demo, 463));
  const scheduledDemos = rows.filter(
    (demo) => !hasFilter(demo, 453) && !hasFilter(demo, 463),
  );

  return { instantDemos, guidedDemos, scheduledDemos };
};

// -----------------------------------------------------------------------------
// Per-user dCloud identity (SSO link)
//
// Tokens never reach the browser. These endpoints only report link state and
// hand back the provider authorization URL to navigate to.
// -----------------------------------------------------------------------------

/**
 * Which provider issued the stored token.
 *
 * `idp` is the portal's own Duo client. `ui_tokens` is minted by dCloud.
 * `tb_idp` is an id.cisco.com token - the kind Topology Builder's own UI sends,
 * and the only one its gateway validates successfully besides a ui-token.
 */
export type DcloudTokenBackend = 'idp' | 'ui_tokens' | 'tb_idp';

/**
 * What the backend says will actually fix an identity failure.
 *
 * The one that matters is the difference between the first two. `sign_in` means an
 * SSO round trip resolves it. `paste_token` means it cannot: the stored token came
 * from the portal's own provider (Duo) and Topology Builder validates bearers
 * against id.cisco.com, so signing in again only re-mints the same unusable token.
 * Never offer sign-in as the remedy for a `paste_token` failure.
 */
export type DcloudLinkRemedy = 'sign_in' | 'paste_token' | 'wait' | 'none';

/** Where the caller can find a Topology Builder token in their own browser. */
export type DcloudTopologyTokenHint = {
  idp_code?: string;
  /** localStorage key holding the access token, e.g. `tb_p_a`. */
  access_token_key?: string;
  refresh_token_key?: string;
  /** Hostname of the provider TBv3 trusts. */
  issuer?: string;
  /** True once a full sign-in against that provider is possible from here. */
  login_enabled?: boolean;
  refreshable?: boolean;
};

export type DcloudUserTokenStatus = {
  enabled: boolean;
  backend?: DcloudTokenBackend | string;
  linked: boolean;
  usable: boolean;
  can_refresh?: boolean;
  /**
   * Narrower than can_refresh: whether the *backend* can do the renewing. An
   * adopted ui-token holds a refresh token dCloud will not redeem for us.
   */
  refresh_supported?: boolean;
  expires_at?: string | null;
  expires_in_seconds?: number | null;
  data_center?: string | null;
  token_type?: string | null;
  scope?: string | null;
  /** True when the token was pasted in rather than obtained by the SSO flow. */
  adopted?: boolean;
  /** True when the stored token is one Topology Builder accepts. */
  topology_ready?: boolean;
  topology_token_hint?: DcloudTopologyTokenHint;
  /**
   * True when there is no stored token *and* the previous one ended for a reason
   * the caller can act on. Distinguishes "your link broke, here is why" from
   * "you have never linked", which otherwise look identical.
   */
  needs_repaste?: boolean;
  /** Why the previous link ended. See DCLOUD_STALE_REASONS. */
  stale_reason?: string | null;
};

/**
 * Explanations for a link that ended on its own.
 *
 * `refresh_token_rotated_away` is the common one for a pasted Topology Builder
 * token: id.cisco.com rotates refresh tokens single-use, so the captured token and
 * the tab it came from invalidate each other the first time either refreshes.
 */
export const DCLOUD_STALE_REASONS: Record<string, string> = {
  refresh_token_rotated_away:
    'Your Topology Builder tab refreshed its own session, which invalidated the token you pasted. They share one single-use refresh token, so only one side can hold it. Paste a fresh one to restore the link.',
};

export type DcloudLinkStatus = {
  email?: string;
  dcloud_user_token: DcloudUserTokenStatus;
  login_url?: string;
  /** Present only when signing in against Topology Builder's provider can work. */
  topology_login_url?: string;
};

export type DcloudLoginStart = {
  authorization_url: string;
  state: string;
  expires_in: number;
  backend: string;
  data_center: string;
  redirect_uri: string;
};

export const DCLOUD_LINK_RESULT_PARAM = 'dcloud_link';

/**
 * Set to 'unavailable' when the link succeeded but the chained dCloud ui-token
 * mint did not. Reported alongside dcloud_link=success, not instead of it.
 */
export const DCLOUD_UI_TOKEN_PARAM = 'dcloud_ui_token';

export const fetchDcloudLinkStatus = async (): Promise<DcloudLinkStatus> => {
  const { data } = await dcloudFetch<DcloudLinkStatus>(
    'GET',
    'dcloud/auth/status/',
    { skipDataCenter: true },
  );

  return data;
};

/**
 * Mint the provider authorization URL. The caller is responsible for the
 * top-level navigation, since SSO cannot complete inside fetch().
 */
export const startDcloudLink = async (
  nextUrl?: string,
  /**
   * Sign in against Topology Builder's own provider instead of the portal's. The
   * backend answers 503 unless that flow is enabled, so gate the call on
   * `topology_login_url` being present in link status.
   */
  backend?: Extract<DcloudTokenBackend, 'tb_idp'>,
): Promise<DcloudLoginStart> => {
  const { data } = await dcloudFetch<DcloudLoginStart>(
    'GET',
    'dcloud/auth/login/',
    {
      params: {
        ...(nextUrl ? { next: nextUrl } : {}),
        ...(backend ? { backend } : {}),
        preferred_data_center: getPreferredDataCenter(),
      },
      skipDataCenter: true,
    },
  );

  return data;
};

export type DcloudUiTokenAdoption = {
  accessToken: string;
  refreshToken?: string;
  /**
   * Which provider the token came from. `tb_idp` is preferred: its refresh works
   * from the server, so the paste is one-off rather than hourly.
   */
  backend?: Extract<DcloudTokenBackend, 'ui_tokens' | 'tb_idp'>;
  /** Seconds. Read from the token's own `exp` claim when omitted. */
  expiresIn?: number;
  preferredDataCenter?: string;
};

/**
 * Hand the backend a token captured from an authenticated browser tab.
 *
 * Neither kind can be obtained server-side. A dCloud ui-token needs a code only
 * dCloud's own login page can produce, and an id.cisco.com token needs a redirect
 * registered on Topology Builder's OAuth client. Copying one out of a signed-in
 * tab is what bridges that until a registration lands.
 *
 * Prefer `backend: 'tb_idp'` with both `tb_<code>_a` and `tb_<code>_r`: that
 * refresh token can be redeemed with nothing but the public client id, so the
 * server keeps the link alive on its own.
 */
export const adoptDcloudUiToken = async (
  adoption: DcloudUiTokenAdoption,
): Promise<DcloudLinkStatus> => {
  const { data } = await dcloudFetch<DcloudLinkStatus>(
    'POST',
    'dcloud/auth/ui-token/',
    {
      skipDataCenter: true,
      body: {
        access_token: adoption.accessToken,
        backend: adoption.backend || 'tb_idp',
        ...(adoption.refreshToken ? { refresh_token: adoption.refreshToken } : {}),
        ...(adoption.expiresIn ? { expires_in: adoption.expiresIn } : {}),
        preferred_data_center:
          adoption.preferredDataCenter || getPreferredDataCenter(),
      },
    },
  );

  return data;
};

/**
 * A console one-liner that copies both Topology Builder tokens at once.
 *
 * Reading two localStorage keys by hand in the devtools inspector is where this
 * flow actually loses people, so the modal hands them this instead. `copy()` is a
 * devtools console builtin, which is exactly where it is meant to be run; the
 * clipboard then holds JSON that parseDcloudTokenPaste understands.
 */
export const buildDcloudTokenCaptureSnippet = (
  hint?: DcloudTopologyTokenHint | null,
): string => {
  const accessKey = hint?.access_token_key || 'tb_p_a';
  const refreshKey = hint?.refresh_token_key || 'tb_p_r';

  return `copy(JSON.stringify({access_token:localStorage.getItem('${accessKey}'),refresh_token:localStorage.getItem('${refreshKey}')}))`;
};

/** Strip the wrappers a token picks up in transit: quotes, and a Bearer prefix. */
const cleanPastedToken = (value: unknown): string => {
  let token = String(value ?? '').trim();

  if (
    (token.startsWith('"') && token.endsWith('"')) ||
    (token.startsWith("'") && token.endsWith("'"))
  ) {
    token = token.slice(1, -1).trim();
  }

  if (/^bearer\s+/i.test(token)) {
    token = token.replace(/^bearer\s+/i, '').trim();
  }

  return token;
};

/**
 * Whatever the caller pasted, as an access/refresh pair.
 *
 * Accepts the JSON the capture snippet produces, a raw token, or a token with a
 * `Bearer ` prefix copied off a request header. The backend cleans its input too,
 * but doing it here lets the modal show what it understood before submitting.
 */
export const parseDcloudTokenPaste = (
  text: string,
): { accessToken: string; refreshToken: string } => {
  const trimmed = String(text || '').trim();

  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed);

      return {
        accessToken: cleanPastedToken(
          parsed.access_token ?? parsed.accessToken ?? parsed.token ?? '',
        ),
        refreshToken: cleanPastedToken(
          parsed.refresh_token ?? parsed.refreshToken ?? '',
        ),
      };
    } catch {
      // Not JSON after all - fall through and treat it as a bare token.
    }
  }

  return { accessToken: cleanPastedToken(trimmed), refreshToken: '' };
};

export const unlinkDcloudAccount = async (): Promise<DcloudLinkStatus> => {
  const { data } = await dcloudFetch<DcloudLinkStatus>(
    'POST',
    'dcloud/auth/unlink/',
    { skipDataCenter: true, body: {} },
  );

  return data;
};

// -----------------------------------------------------------------------------
// Session dates
// -----------------------------------------------------------------------------

/**
 * dCloud declares session start/stop as bare strings with no format. Reads
 * accept epoch milliseconds and ISO 8601 so an upstream change cannot blank the
 * session list; writes go out in this format. Flip this constant if dCloud
 * rejects a schedule/edit payload.
 */
export type DcloudDateWireFormat = 'epoch-millis' | 'iso';

// Asserted rather than annotated so the compiler keeps the union instead of narrowing the
// const to its initializer, which would make the other branch below look unreachable.
export const DCLOUD_DATE_WIRE_FORMAT = 'epoch-millis' as DcloudDateWireFormat;

export const parseDcloudDate = (value: any): Date | null => {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();

  // Seconds and milliseconds are both seen in the wild, and token expiries are
  // fractional seconds (issued_at + expires_in), which Date() cannot parse as a
  // string. Integer digits decide the unit: 10 or fewer is seconds.
  if (/^-?\d+(\.\d+)?$/.test(raw)) {
    const numeric = Number(raw);
    const integerDigits = raw.replace('-', '').split('.')[0].length;
    const millis = integerDigits <= 10 ? numeric * 1000 : numeric;
    const fromEpoch = new Date(millis);

    return Number.isNaN(fromEpoch.getTime()) ? null : fromEpoch;
  }

  const parsed = new Date(raw);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDcloudDateForWire = (value: Date | string | null): string => {
  const date = parseDcloudDate(value);

  if (!date) return '';

  return DCLOUD_DATE_WIRE_FORMAT === 'iso'
    ? date.toISOString()
    : String(date.getTime());
};

export const formatDcloudDate = (value: any): string => {
  const date = parseDcloudDate(value);

  if (!date) return '—';

  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/** Value for an <input type="datetime-local">, in the browser's local zone. */
export const toDateTimeLocalValue = (value: any): string => {
  const date = parseDcloudDate(value);

  if (!date) return '';

  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

// -----------------------------------------------------------------------------
// Sessions
// -----------------------------------------------------------------------------

export type DcloudSessionState = 'active' | 'scheduled' | 'ended' | 'error';

export type DcloudSession = {
  id: number | null;
  /** The demo this session was created from, so it can be scheduled again. */
  demoId: number | null;
  name: string;
  demoName: string;
  owner: string;
  start: Date | null;
  stop: Date | null;
  state: DcloudSessionState;
  statusCode: number | null;
  errorState: boolean;
  canReset: boolean;
  canRequestExtension: boolean;
  canEdit: boolean;
  launchUrl: string | null;
  scenario: string | null;
  dataCenter: string;
  raw: any;
};

const idFromHref = (href: any): number | null => {
  const match = String(href || '').match(/(\d+)(?:\/)?$/);

  if (!match) return null;

  const parsed = Number(match[1]);

  return Number.isFinite(parsed) ? parsed : null;
};

export const getDcloudSessionId = (session: any): number | null => {
  if (!session) return null;

  if (Number.isFinite(session.uid)) return Number(session.uid);
  if (Number.isFinite(session.id)) return Number(session.id);

  return idFromHref(session?.id?.href);
};

export const getDcloudDemoId = (demo: any): number | null => {
  if (!demo) return null;

  if (Number.isFinite(demo.uid)) return Number(demo.uid);
  if (Number.isFinite(demo.demoId)) return Number(demo.demoId);
  if (Number.isFinite(demo.contentId)) return Number(demo.contentId);
  if (Number.isFinite(demo.id)) return Number(demo.id);

  return idFromHref(demo?.id?.href);
};

const findLaunchUrl = (session: any): string | null => {
  const links = Array.isArray(session?.links) ? session.links : [];

  const preferred =
    links.find((link: any) => /session|launch|view/i.test(String(link?.rel || link?.name || ''))) ||
    links[0];

  const href = preferred?.href || session?.href || null;

  // Only surface links the browser can actually open.
  return typeof href === 'string' && /^https?:\/\//i.test(href) ? href : null;
};

export const normalizeDcloudSession = (
  raw: any,
  dataCenter = getPreferredDataCenter(),
): DcloudSession => {
  const start = parseDcloudDate(raw?.start);
  const stop = parseDcloudDate(raw?.stop);
  const now = Date.now();

  let state: DcloudSessionState = 'scheduled';

  if (raw?.errorState) {
    state = 'error';
  } else if (stop && stop.getTime() < now) {
    state = 'ended';
  } else if (start && start.getTime() <= now) {
    state = 'active';
  }

  return {
    id: getDcloudSessionId(raw),
    demoId: Number.isFinite(raw?.parentId)
      ? Number(raw.parentId)
      : getDcloudDemoId(raw?.parent),
    name: raw?.name || raw?.parentDemoName || 'dCloud session',
    demoName: raw?.parentDemoName || raw?.parent?.name || '',
    owner: raw?.owner || '',
    start,
    stop,
    state,
    statusCode: Number.isFinite(raw?.status) ? Number(raw.status) : null,
    errorState: Boolean(raw?.errorState),
    canReset: Boolean(raw?.canReset),
    canRequestExtension: Boolean(raw?.canRequestExtension),
    // Ended sessions are immutable upstream, so hide the edit affordance.
    canEdit: state !== 'ended',
    launchUrl: findLaunchUrl(raw),
    scenario: raw?.scenario?.displayName || null,
    dataCenter,
    raw,
  };
};

export const partitionDcloudSessions = (sessions: DcloudSession[]) => {
  const current = sessions.filter((session) => session.state !== 'ended');
  const previous = sessions.filter((session) => session.state === 'ended');

  const byStart = (a: DcloudSession, b: DcloudSession) =>
    (a.start?.getTime() ?? 0) - (b.start?.getTime() ?? 0);

  return {
    current: [...current].sort(byStart),
    previous: [...previous].sort(byStart).reverse(),
  };
};

export type DcloudSessionsResult = {
  sessions: DcloudSession[];
  authSource: DcloudAuthSource;
};

export const fetchDcloudSessions = async (
  dataCenter?: string,
): Promise<DcloudSessionsResult> => {
  const dc = dataCenter || getPreferredDataCenter();

  const { data, authSource } = await dcloudFetch<DcloudSearchResponse>(
    'GET',
    'dcloud/sessions',
    { params: { expand: 'all' }, dataCenter: dc },
  );

  return {
    sessions: normalizeDcloudRows(data).map((row) => normalizeDcloudSession(row, dc)),
    authSource,
  };
};

export const fetchDcloudSession = async (
  sessionId: number,
  dataCenter?: string,
): Promise<DcloudSession> => {
  const dc = dataCenter || getPreferredDataCenter();

  const { data } = await dcloudFetch<any>('GET', `dcloud/sessions/${sessionId}`, {
    params: { expand: 'all' },
    dataCenter: dc,
  });

  return normalizeDcloudSession(data, dc);
};

/**
 * Previously held sessions. dCloud keeps these on /history rather than
 * /sessions, which only returns what is still scheduled or running.
 */
export const fetchDcloudSessionHistory = async (
  dataCenter?: string,
): Promise<DcloudSession[]> => {
  const dc = dataCenter || getPreferredDataCenter();

  const { data } = await dcloudFetch<{ history?: any[] }>('GET', 'dcloud/history', {
    dataCenter: dc,
  });

  const rows = Array.isArray(data?.history) ? data.history : normalizeDcloudRows(data);

  return rows.map((row: any) => {
    const dates = row?.dates || {};

    return normalizeDcloudSession(
      {
        ...row,
        name: row?.name,
        parentDemoName: row?.parent?.name || row?.parentDemoName,
        start: dates.start ?? dates.created ?? row?.start,
        stop: dates.stop ?? dates.end ?? row?.stop,
      },
      dc,
    );
  });
};

export type DcloudMetric = { name: string; value: string };

export type DcloudScheduleRequest = {
  demoId: number;
  start: string;
  stop: string;
  count?: number;
  metrics?: DcloudMetric[];
  justification?: string;
  description?: string;
  scenario?: string;
  contentPoolOptionId?: string;
  endpoints?: string[];
  contentExport?: boolean;
  accessType?: string;
};

export type DcloudScheduleResponse = {
  success?: boolean;
  message?: string[];
  sessions?: any[];
  eventUid?: number;
};

export const scheduleDcloudSession = async (
  payload: DcloudScheduleRequest,
  dataCenter?: string,
): Promise<DcloudScheduleResponse> => {
  const { data } = await dcloudFetch<DcloudScheduleResponse>(
    'POST',
    'dcloud/sessions/schedule',
    { body: payload, dataCenter },
  );

  // dCloud answers 200 with success:false and a message array on refusal.
  if (data && data.success === false) {
    throw new Error(
      (Array.isArray(data.message) ? data.message.join(' ') : String(data.message || '')) ||
        'dCloud declined to schedule this session.',
    );
  }

  return data;
};

export type DcloudEditSessionRequest = {
  name?: string;
  start?: string;
  stop?: string;
  scenario?: string;
  contentPoolId?: string;
  endpointKitIds?: string[];
  sharedWith?: { userId: string; metrics?: DcloudMetric[] }[];
};

export const updateDcloudSession = async (
  sessionId: number,
  payload: DcloudEditSessionRequest,
  dataCenter?: string,
): Promise<any> => {
  const { data } = await dcloudFetch<any>('PUT', `dcloud/sessions/${sessionId}`, {
    body: payload,
    dataCenter,
  });

  if (data && data.success === false) {
    throw new Error(String(data.message || 'dCloud declined the session update.'));
  }

  return data;
};

/**
 * dCloud has no session DELETE. Ending a session is the destructive action:
 * it tears down the pod and frees the reservation.
 */
export const endDcloudSession = async (
  sessionId: number,
  dataCenter?: string,
): Promise<any> => {
  const { data } = await dcloudFetch<any>('PUT', `dcloud/sessions/${sessionId}/end`, {
    body: {},
    dataCenter,
  });

  if (data && data.success === false) {
    throw new Error(
      (Array.isArray(data.message) ? data.message.join(' ') : String(data.message || '')) ||
        'dCloud declined to end this session.',
    );
  }

  return data;
};

export const extendDcloudSession = async (
  session: DcloudSession,
  extraMinutes: number,
  dataCenter?: string,
): Promise<any> => {
  if (!session.id || !session.stop) {
    throw new Error('This session cannot be extended.');
  }

  const stop = new Date(session.stop.getTime() + extraMinutes * 60 * 1000);

  return updateDcloudSession(
    session.id,
    {
      start: formatDcloudDateForWire(session.start),
      stop: formatDcloudDateForWire(stop),
    },
    dataCenter || session.dataCenter,
  );
};

// -----------------------------------------------------------------------------
// Scheduling metadata
// -----------------------------------------------------------------------------

export type DcloudSchedulingOption = {
  value: string;
  label: string;
  type: string;
  mandatory: boolean;
  values: { value: string; label: string; children?: string[] }[];
};

export type DcloudSchedulingOptions = {
  root?: string;
  schedulingOptions: DcloudSchedulingOption[];
};

/**
 * Per-user scheduling metrics dCloud requires on a schedule request (purpose,
 * customer, and similar). Mandatory options must be answered or dCloud refuses.
 */
export const fetchDcloudSchedulingOptions = async (
  dataCenter?: string,
): Promise<DcloudSchedulingOptions> => {
  const { data } = await dcloudFetch<DcloudSchedulingOptions>(
    'GET',
    'dcloud/users/current/scheduling-options',
    { dataCenter },
  );

  return {
    root: data?.root,
    schedulingOptions: Array.isArray(data?.schedulingOptions)
      ? data.schedulingOptions
      : [],
  };
};

export const searchDcloudCatalog = async (
  text: string,
  dataCenter?: string,
): Promise<any[]> => {
  const search = String(text || '').trim();

  if (!search) return [];

  const { data } = await dcloudFetch<DcloudSearchResponse>(
    'GET',
    'dcloud/catalog/search',
    { params: { text: search }, dataCenter },
  );

  return normalizeDcloudRows(data);
};

// -----------------------------------------------------------------------------
// Demo contents
//
// What a lab is published from. dCloud calls these "contents"; a saved one is a
// demo that can be scheduled, and scheduling it produces the sessions the lab
// runtime matches against.
//
// Per data centre, always. A demo uid is not global - the same lab saved in RTP
// and in London is two uids on two topologies, which is the whole reason
// LabConfiguration exists as a separate row per data centre.
// -----------------------------------------------------------------------------

/**
 * One dCloud demo, as `/api/contents` returns it.
 *
 * Only the fields a lab publication is built out of are named. The index
 * signature keeps the rest: dCloud owns this payload, and a picker that dropped
 * unknown fields would silently lose whatever they add next.
 */
export type DcloudDemoContent = {
  /** What a session's `parentId` points at, and what `LabConfiguration.demo_uid` stores. */
  uid: number;
  name: string;
  description?: string;
  owner?: string;
  type?: string;
  /** `RTP-DEM-1387401`. The prefix names the data centre the demo lives in. */
  guid?: string;
  topologyUid?: string;
  topologyVersionUid?: string;
  /** `saved`, `edited`, `promoted`. A demo has to be saved before it can be scheduled. */
  state?: string[];
  local?: boolean;
  publicContent?: boolean;
  scenario?: string | null;
  dates?: { published?: string; saved?: string };
  defaults?: {
    hasDemoLimits?: boolean;
    maxDuration?: number;
    concurrentCount?: number;
    eventsEnabled?: boolean;
  };
  links?: { rel: string; href: string }[];
  [key: string]: any;
};

/**
 * The demos a lab can be published from in one data centre.
 *
 * `state=saved` because an unsaved demo cannot be scheduled, so publishing a lab
 * against one would produce a configuration that never matches a session.
 * `expand=sharedWith` because a demo owned by somebody else and shared with this
 * engineer is just as publishable as their own, and without the expansion the
 * share list is absent rather than empty - the same distinction the session sync
 * already depends on.
 */
export const fetchDcloudDemoContents = async (
  dataCenter?: string,
): Promise<DcloudDemoContent[]> => {
  const { data } = await dcloudFetch<DcloudSearchResponse>('GET', 'dcloud/contents', {
    params: { state: 'saved', expand: 'sharedWith' },
    dataCenter,
  });

  return normalizeDcloudRows(data).filter(
    (row: any) => row && (row.uid ?? null) !== null,
  ) as DcloudDemoContent[];
};

/**
 * The data centre a demo's guid says it lives in, as a `DCLOUD_DATA_CENTERS` key.
 *
 * Worth checking rather than trusting: the proxy sends the request to whichever
 * data centre it was told, and a demo uid pasted from another one comes back as a
 * 404 at schedule time - long after the publication looked correct in the admin UI.
 */
export const dcloudDemoDataCenterKey = (demo: DcloudDemoContent): string | null => {
  const prefix = String(demo?.guid || '').split('-')[0];

  if (!prefix) return null;

  const key = prefix.toLowerCase();

  return DCLOUD_DATA_CENTERS.some((entry) => entry.value === key) ? key : null;
};

/** Where dCloud's own topology editor opens this demo, when it offered a link. */
export const dcloudDemoEditTopologyUrl = (demo: DcloudDemoContent): string | null =>
  (demo?.links || []).find((link) => link?.rel === 'editTopology')?.href || null;

export type DcloudCalendarSlot = {
  start: any;
  stop?: any;
  end?: any;
  available?: boolean;
  [key: string]: any;
};

/**
 * Availability for a demo over a window, used to warn before a schedule attempt
 * that dCloud would reject for lack of capacity.
 */
export const fetchDcloudDemoCalendar = async (
  demoId: number,
  start: Date,
  end: Date,
  dataCenter?: string,
): Promise<{ timeslots: DcloudCalendarSlot[]; metaData?: any }> => {
  const { data } = await dcloudFetch<any>(
    'GET',
    `dcloud/demos/${demoId}/calendar`,
    {
      params: {
        start: formatDcloudDateForWire(start),
        end: formatDcloudDateForWire(end),
      },
      dataCenter,
    },
  );

  return {
    timeslots: Array.isArray(data?.timeslots) ? data.timeslots : [],
    metaData: data?.metaData,
  };
};

export { normalizeDcloudRows, cleanProductName };
